import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const ReferralCX: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "ReferralCX.sections.executiveSummary" },
    { id: "current-state", labelKey: "ReferralCX.sections.currentState" },
    { id: "risks-legacy", labelKey: "ReferralCX.sections.risksLegacy" },
    { id: "guardrails", labelKey: "ReferralCX.sections.guardrails" },
    { id: "ai-analytics", labelKey: "ReferralCX.sections.aiAnalytics" },
    { id: "data-insights", labelKey: "ReferralCX.sections.dataInsights" },
    { id: "implementation", labelKey: "ReferralCX.sections.implementation" },
    { id: "conclusion", labelKey: "ReferralCX.sections.conclusion" },
    { id: "references", labelKey: "ReferralCX.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | ReferralCX
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Referrals remain the "front door" to specialty care, yet referral processes are often outdated, inefficient, and error-prone. Patients frequently experience delays, lost information, or misdirected referrals — leading to frustration, poorer outcomes, and unnecessary costs.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="12 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "The Future of Referral Management in Healthcare", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "current-state" && "🤖 1. The Current State of Referrals"}
                {s.id === "risks-legacy" && "📊 2. Risks of Legacy Referral Systems"}
                {s.id === "guardrails" && "💡 3. Guardrails for Smarter Referrals"}
                {s.id === "ai-analytics" && "⚠️ 4. The Role of AI and Analytics"}
                {s.id === "data-insights" && "🛠️ 5. Data-Driven Insights for the Future"}
                {s.id === "implementation" && "🛠️ 6. Implementation Roadmap"}
                {s.id === "conclusion" && "✅ 6. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Referrals remain the "front door" to specialty care, yet referral processes are often outdated, inefficient, and error-prone. Patients frequently experience delays, lost information, or misdirected referrals — leading to frustration, poorer outcomes, and unnecessary costs.
                    </p>
                    <br />
                    <p>
                      ReferralCX, part of the EceptionistCX platform, transforms referrals by embedding structured workflows, AI-driven guardrails, and data-driven analytics into the process. This ensures referrals are routed to the right place the first time, eliminates administrative waste, and creates a foundation for smarter, more equitable healthcare delivery.
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> 1 in 4 referrals are sent to the wrong specialty due to lack of structured guidance for referring providers.</p>
                    </div>
                  </>
                )}
                {s.id === "current-state" && (
                  <>
                    <p>
                      Despite digital health progress, many referral pathways still rely heavily on fax machines, phone calls, and unstructured notes.
                    </p>
                    <br />
                    <ul>
                      <li>- In the U.S., 46% of referrals never result in a completed appointment, contributing to referral leakage and gaps in care [1].</li>
                      <li>- Research shows that 1 in 4 referrals are sent to the wrong specialty due to lack of structured guidance for referring providers [2].</li>
                      <li>- The NHS continues to report bottlenecks from misdirected referrals, leading to extended wait times for patients [3].</li>
                    </ul>
                  </>
                )}
                {s.id === "risks-legacy" && (
                  <>
                    <p>
                      Reliance on manual processes creates systemic problems:
                    </p>
                    <br />
                    <ul>
                      <li>- Misdirected referrals force patients to "bounce" between services before receiving care.</li>
                      <li>- Delays in diagnosis and treatment increase risk for patients with serious conditions.</li>
                      <li>- Administrative overhead consumes staff time in re-routing and clarifying referrals.</li>
                      <li>- Referral leakage results in lost revenue for health systems.</li>
                    </ul>
                  </>
                )}
                {s.id === "guardrails" && (
                  <>
                    <p>
                      ReferralCX addresses these challenges with structured workflows and embedded guardrails that guide referrers at the point of entry:
                    </p>
                    <br />
                    <ul>
                      <li>- Structured digital forms capture the right information from the beginning.</li>
                      <li>- Guardrails provide decision support to help referring providers send patients to the correct specialty or service.</li>
                      <li>- Configurable workflows allow organizations to align referral pathways with internal capacity and clinical guidelines.</li>
                    </ul>
                    <br />
                    <p>
                      The result: referrals are routed correctly the first time, improving access, efficiency, and equity.
                    </p>
                  </>
                )}
                {s.id === "ai-analytics" && (
                  <>
                    <p>
                      AI extends the impact of digital referral management beyond automation:
                    </p>
                    <br />
                    <ul>
                      <li>- AI triage models can prioritize urgent cases (e.g., suspected cancer, cardiac events).</li>
                      <li>- Predictive analytics forecast demand for certain specialties, helping health systems align staffing and resources.</li>
                      <li>- Data capture enables organizations to analyze referral patterns, identify bottlenecks, and make informed operational decisions.</li>
                    </ul>
                    <br />
                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Impact:</span> According to Frost & Sullivan, predictive analytics could save the U.S. healthcare system up to $70 billion annually in operational efficiencies [4].</p>
                    </div>
                  </>
                )}
                {s.id === "data-insights" && (
                  <>
                    <p>
                      Every referral generates operational data that can be leveraged for improvement:
                    </p>
                    <br />
                    <ul>
                      <li>- Volume by specialty → informs hiring and resource planning.</li>
                      <li>- Time to acceptance/appointment → highlights bottlenecks.</li>
                      <li>- Referral leakage metrics → quantify lost revenue opportunities.</li>
                      <li>- Equity metrics → identify disparities in access to care.</li>
                    </ul>
                    <br />
                    <p>
                      ReferralCX equips health systems with actionable data to continuously optimize referral pathways.
                    </p>
                  </>
                )}
                {s.id === "implementation" && (
                  <>
                    <p>
                      Adopting digital referral management requires a structured approach:
                    </p>
                    <br />
                    <ol>
                      <li>1. Assessment – Map existing referral pathways and leakage points.</li>
                      <li>2. Integration – Connect ReferralCX with existing EHR and scheduling systems.</li>
                      <li>3. Guardrail Deployment – Embed decision support and clinical guidelines.</li>
                      <li>4. Automation – Streamline communication between providers and departments.</li>
                      <li>5. Scale and Optimize – Use analytics to refine processes and expand across specialties.</li>
                    </ol>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      The referral process is one of healthcare's most important yet most broken workflows. By moving from fax and phone to structured, AI-driven workflows, health systems can dramatically improve access, efficiency, and patient outcomes.
                    </p>
                    <br />
                    <p>
                      ReferralCX provides the tools to get referrals right the first time, while capturing data that drives long-term improvements in capacity planning, equity, and care delivery.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. Barnett, M.L., et al. Referral Management and Specialty Care Coordination: Analysis of Leakage in U.S. Healthcare. JAMA Network Open, 2021.</li>
                      <li>2. O'Malley, A.S., et al. Referral Misallocation and Specialty Misdirection in Primary Care. Annals of Family Medicine, 2020.</li>
                      <li>3. NHS England. Referral to Treatment (RTT) Statistics and Reports. 2023.</li>
                      <li>4. Frost & Sullivan. Predictive Analytics in Healthcare: Market Potential and Cost Savings. 2021.</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Referral Leakage (%)</li>
                        <li>• Referral Accuracy (%)</li>
                        <li>• Projected Savings from Predictive Analytics (USD B)</li>
                      </ul>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Referral Leakage Analysis</h5>
                        <img 
                          src="/Image/WhitePapers/ReferralCX_WhitePaper_New_Format/image1.png" 
                          alt="Referral Leakage Analysis Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Referral Accuracy Metrics</h5>
                        <img 
                          src="/Image/WhitePapers/ReferralCX_WhitePaper_New_Format/imag2.png" 
                          alt="Referral Accuracy Metrics Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Projected Savings from Predictive Analytics</h5>
                        <img 
                          src="/Image/WhitePapers/ReferralCX_WhitePaper_New_Format/image3.png" 
                          alt="Projected Savings from Predictive Analytics Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how AI-powered referral management transforms healthcare workflows from fax-based processes to structured, data-driven systems.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>46% of referrals never result in appointments</li>
                <li>1 in 4 referrals sent to wrong specialty</li>
                <li>$70B potential annual savings from AI</li>
                <li>Structured workflows eliminate waste</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "current-state" && "The Current State of Referrals"}
                      {s.id === "risks-legacy" && "Risks of Legacy Referral Systems"}
                      {s.id === "guardrails" && "Guardrails for Smarter Referrals"}
                      {s.id === "ai-analytics" && "The Role of AI and Analytics"}
                      {s.id === "data-insights" && "Data-Driven Insights for the Future"}
                      {s.id === "implementation" && "Implementation Roadmap"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Digital Front Doors in Healthcare", description: "Why scheduling matters more than ever for patient experience and provider efficiency.", route: "/whitepapers/scheduling-cx" },
              { title: "AI in Healthcare Operations", description: "Practical applications for smarter collaboration and better care delivery.", route: "/whitepapers/ai-in-healthcare-operations" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs.", route: "/whitepapers/integrated-care" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows"
        pdfPath="/PDF/WhitePapers/ReferralCX_WhitePaper.pdf"
      />
    </div>
  )
}

export default ReferralCX
